import type { NotificationsPerGame, SpecialEventTargeting, TargetingRule } from "domains/specialEvents/pages/main"
import { GameIdsMap, gameNames } from "abstract/constants/game-ids"

const TARGETING_SOURCES = ["game", "launcher"]
const TARGETING_MATCHES = ["equals", "contains"]
const TARGETING_RULE_KEYS = ["source", "path", "values", "match"]

const DEFAULT_SOURCE: TargetingRule["source"] = "game"
const DEFAULT_MATCH: TargetingRule["match"] = "equals"

// The app release that first honours `targeting`. Left undefined until a build carrying the client
// half ships. A boolean cannot express the real state: adoption is never 100 %, so even after the
// release lands an older client ignores the key and runs the campaign in every mode. Every gate
// state on the new client excludes targeted campaigns rather than widening them; the over-delivery
// risk is always the old client.
export const TARGETING_CLIENT_VERSION: string | undefined = undefined

export const TARGETING_FIELD_NOTE = TARGETING_CLIENT_VERSION === undefined
  ? "Not live yet — no released app version honours this, so a targeted campaign still runs in every "
    + "mode of the game."
  : `Honoured by app ${TARGETING_CLIENT_VERSION} and later. Older clients ignore it and run this `
    + "campaign in every mode, so expect out-of-mode delivery until adoption completes."

// Raised on every save that carries targeting, not only the save that introduces it: the risk is
// the installed base, which changes without anyone touching this campaign.
export const describeTargetingSupport = (targetedGames: string[]): string | undefined => {
  if (!targetedGames.length) return undefined

  const games = targetedGames.join(", ")

  return TARGETING_CLIENT_VERSION === undefined
    ? `Mode targeting is set for ${games}, but no released app version honours it yet, so this campaign `
      + "currently serves in every mode of those games."
    : `Mode targeting is set for ${games}. Only app ${TARGETING_CLIENT_VERSION} and later honour it; `
      + "older clients still serve this campaign in every mode."
}

// What the editor holds: an object once it parses, the raw text while it does not.
export type TargetingFieldValue = SpecialEventTargeting | string

type KnownPath = {
  source: TargetingRule["source"];
  path: string;
  // The key exists but a rule on it cannot be trusted; explains why.
  unsafe?: string;
  // The key works, but names a trap worth repeating.
  caution?: string;
  // Values seen in recorded sessions or taken from the buff.app trackers; used only to spot typos.
  values?: string[];
}

// The client keys every fact as `category.key` off the payload, so these are categories, not the
// feature names they arrive under. Checked before the per-game map so an unmeasured game gets them.
const LAUNCHER_ONLY_CATEGORIES = ["lobby_info", "game_flow", "end_game_lol", "end_game_tft"]

// Categories nothing ever writes. `end_game` is requested as a *feature*, but its keys arrive under
// end_game_lol / end_game_tft, so no fact is ever keyed under `end_game` itself.
const UNBOUND_CATEGORIES = ["end_game", "champ_select", "lcu_info", "clash"]

// Only the League family resolves to a launcher the client can read facts from.
const LAUNCHER_CAPABLE_GAMES = [GameIdsMap.LOL, GameIdsMap.LOL_TFT, GameIdsMap.LOL_ARENA].map(String)

// Checked against the buff.app trackers and recorded GEP sessions. A game absent from this map
// is never warned about: config-driven targeting has to work for games nobody has measured yet.
const KNOWN_PATHS_BY_GAME: Record<string, KnownPath[]> = {
  [GameIdsMap.FORTNITE]: [
    {
      source: "game",
      path: "game_info.title_mnemonic",
      values: [
        "experience_br",
        "playlist_juno",
        "experience_reload",
        "experience_og",
        "experience_blitz",
        "ref_settings_br_unranked_nobuild",
      ],
    },
    { source: "game", path: "game_info.game_type", values: ["Battle Royal", "Lego Fortnite", "OG Battle Royal", "Reload"] },
    { source: "game", path: "match_info.map", values: ["Hera_V2_Terrain", "Hera_Terrain", "Juno_World_Inception"] },
    {
      source: "game",
      path: "match_info.creative_map",
      unsafe: "match_info.creative_map is a stringified JSON object (map_id, title, account_id, creative_mode_version), "
        + "not a map name, and account_id differs per player, so no rule on it can match. Ask engineering before "
        + "targeting a creative map.",
    },
    {
      source: "game",
      path: "match_info.mode",
      unsafe: "match_info.mode carries two unrelated value spaces on the same key — squad size (Solo, Duo, Trio) and "
        + "playlist mnemonics — and flips between them within milliseconds while the feature binds. "
        + "Target game_info.title_mnemonic or game_info.game_type instead.",
    },
  ],
  [GameIdsMap.ROBLOX]: [
    {
      source: "game",
      path: "match_info.universe_id",
      caution: "Roblox's provider cold-starts slowly and the tracker itself waits up to 5s for universe_id, well "
        + "past the client's seed budget, so a rule on it can miss the campaign entirely on a cold launch.",
      values: ["5995470825", "9507134447"],
    },
    { source: "game", path: "match_info.game_id" },
  ],
  [GameIdsMap.MINECRAFT_PC]: [
    { source: "game", path: "match_info.server" },
    { source: "game", path: "match_info.dimension", values: ["overworld", "nether", "the_end"] },
    { source: "game", path: "match_info.biome" },
    {
      source: "game",
      path: "game_info.world_id",
      unsafe: "world_id is an opaque token rather than a world name, so a rule on it matches only the players it was "
        + "copied from. Target match_info.server instead.",
    },
  ],
  [GameIdsMap.ROCKET_LEAGUE]: [
    {
      source: "game",
      path: "match_info.arena",
      caution: "arena also carries menu states (MENU_Main_p, JoinGameTransition). The client reads the latest "
        + "value, so at match start it can still be the menu rather than the arena.",
      values: ["UtopiaStadium_P",
        "Paname_Dusk_P",
        "NeoTokyo_Arcade_P",
        "EuroStadium_Dusk_P",
        "CHN_Stadium_P",
        "Labs_4v4_Arena15_Retro_P",
        "cs_p"],
    },
    {
      source: "game",
      path: "matchInfo.gameType",
      caution: "gameType is the playlist name as the client localizes it, so list the value for every language you "
        + "expect, or the rule excludes those players. It is the key the client's own mode handling reads.",
      values: ["Standard", "Doubles", "Quads", "Private Match", "Training"],
    },
    {
      source: "game",
      path: "matchInfo.ranked",
      values: ["true", "false"],
    },
    {
      source: "game",
      path: "matchInfo.matchType",
      values: ["Lobby", "Private", "Online", "Offline"],
    },
    {
      source: "game",
      path: "matchInfo.gameMode",
      caution: "the client's Rocket League tracker marks gameMode as unreliable on the Overwolf side and uses "
        + "gameType instead.",
      values: ["Soccar", "Breakout", "Basketball", "Hockey", "Items", "volleyball", "playTest", "Unknown"],
    },
  ],
  ...lolFamilyPaths(),
}

// All three League ids resolve to the same launcher and game scopes, so they read the same keys and
// differ only in the values that identify them.
function lolFamilyPaths(): Record<string, KnownPath[]> {
  const paths = (selectedGame: string, queueIds?: string[]): KnownPath[] => [
    { source: "launcher", path: "lobby_info.queueId", values: queueIds },
    { source: "launcher", path: "game_info.selected_game", values: [selectedGame] },
    { source: "game", path: "match_info.game_mode", values: ["tft"] },
    {
      source: "launcher",
      path: "end_game_lol.lol_end_game_stats",
      unsafe: "this key holds a stringified JSON blob of the whole end-game scoreboard, not a value to compare.",
    },
    {
      source: "launcher",
      path: "end_game_tft.tft_end_game_stats",
      unsafe: "this key holds a stringified JSON blob of the whole end-game scoreboard, not a value to compare.",
    },
  ]

  return {
    // The queue ids the client itself treats as supported League modes.
    [GameIdsMap.LOL]: paths("lol", ["400", "420", "430", "440", "490", "700", "100", "450", "720", "2400", "325", "480"]),
    // The queue ids the client uses to re-stamp a match as TFT.
    [GameIdsMap.LOL_TFT]: paths("tft", ["1090", "1100", "1130", "1160", "2200", "6110", "6120", "1210", "1220"]),
    // Arena is recognised by selected_game; it has no queue id of its own anywhere in the client.
    [GameIdsMap.LOL_ARENA]: paths("arena"),
  }
}

export const TARGETING_PLACEHOLDER = `{
  "rules": [
    {
      "source": "game",
      "path": "game_info.title_mnemonic",
      "values": ["playlist_juno"],
      "match": "equals"
    }
  ]
}`

export class TargetingError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "TargetingError"
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === "object" && value !== null && !Array.isArray(value)
)

const listUnknownKeys = (value: Record<string, unknown>, allowedKeys: string[]) => (
  Object.keys(value).filter(key => !allowedKeys.includes(key))
)

// The editor refuses an unknown rule key so a typo is caught while it is still being typed. The
// client ignores it (targetingRules.ts parseRule at buff.app e82657569), so the same strictness
// applied to a value ALREADY in config would report a campaign the client serves as dark.
type ParseRuleOptions = { rejectUnknownKeys?: boolean }

const parseRule = (rule: unknown, index: number, { rejectUnknownKeys = true }: ParseRuleOptions = {}): TargetingRule => {
  const label = `rules[${index}]`

  if (!isRecord(rule)) throw new TargetingError(`${label} must be an object.`)

  const unknownKeys = listUnknownKeys(rule, TARGETING_RULE_KEYS)
  if (rejectUnknownKeys && unknownKeys.length) {
    throw new TargetingError(
      `${label} has unsupported keys: ${unknownKeys.join(", ")}. Allowed: ${TARGETING_RULE_KEYS.join(", ")}.`,
    )
  }
  if (typeof rule.path !== "string" || !rule.path.trim()) {
    throw new TargetingError(`${label}.path must be a non-empty string, e.g. "game_info.title_mnemonic".`)
  }
  if (!Array.isArray(rule.values)
    || !rule.values.length
    || rule.values.some(value => (
      (typeof value !== "string" && typeof value !== "number") || !String(value).trim()
    ))) {
    throw new TargetingError(`${label}.values must be a non-empty array of non-empty strings.`)
  }
  if (rule.source !== undefined && !TARGETING_SOURCES.includes(rule.source as string)) {
    throw new TargetingError(`${label}.source must be ${TARGETING_SOURCES.join(" or ")}.`)
  }
  if (rule.match !== undefined && !TARGETING_MATCHES.includes(rule.match as string)) {
    throw new TargetingError(`${label}.match must be ${TARGETING_MATCHES.join(" or ")}.`)
  }

  // Written out rather than left to a client-side default, so the stored campaign says what it means.
  return {
    source: (rule.source as TargetingRule["source"]) || DEFAULT_SOURCE,
    path: rule.path.trim(),
    values: (rule.values as (string | number)[]).map(value => String(value).trim()),
    match: (rule.match as TargetingRule["match"]) || DEFAULT_MATCH,
  }
}

export const parseTargeting = (value: unknown): SpecialEventTargeting | undefined => {
  if (value === undefined || value === null) return undefined

  let candidate: unknown = value

  if (typeof value === "string") {
    if (!value.trim()) return undefined

    try {
      candidate = JSON.parse(value)
    } catch (error) {
      const detail = error instanceof Error ? ` ${error.message}.` : ""

      throw new TargetingError(`This is not valid JSON. Check for missing commas, quotes, or brackets.${detail}`)
    }
  }

  if (!isRecord(candidate)) throw new TargetingError("Targeting must be an object like { \"rules\": [ ... ] }.")

  if (!Array.isArray(candidate.rules)) throw new TargetingError("Targeting must contain a \"rules\" array.")
  if (!candidate.rules.length) {
    throw new TargetingError(
      "An empty \"rules\" array stops this campaign serving on this game entirely. Clear the field instead.",
    )
  }

  return { rules: candidate.rules.map((rule, index) => parseRule(rule, index)) }
}

export const stringifyTargeting = (value: unknown): string => {
  if (typeof value === "string") return value
  if (value === undefined || value === null) return ""

  return JSON.stringify(value, null, 2)
}

const sharesValue = (left: TargetingRule, right: TargetingRule) => left.values.some(value => (
  right.values.some(otherValue => otherValue.toLowerCase() === value.toLowerCase())
))

// Rules are ANDed, so two "equals" rules on one path with no shared value never both hold.
const getUnsatisfiableRulePairs = (rules: TargetingRule[]): string[] => rules.flatMap((rule, index) => {
  if (rule.match !== "equals") return []

  const clash = rules.findIndex((other, otherIndex) => otherIndex > index
    && other.match === "equals"
    && other.source === rule.source
    && other.path === rule.path
    && !sharesValue(rule, other))

  return clash === -1 ? [] : [
    `rules[${index}] and rules[${clash}] both require ${rule.path} to equal different values, so neither can ever match. `
    + "Put every qualifying value in one rule's values.",
  ]
})

const editDistance = (left: string, right: string): number => {
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index)

  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    let diagonal = previous[0]
    previous[0] = leftIndex

    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      const candidate = left[leftIndex - 1] === right[rightIndex - 1]
        ? diagonal
        : Math.min(diagonal, previous[rightIndex], previous[rightIndex - 1]) + 1
      diagonal = previous[rightIndex]
      previous[rightIndex] = candidate
    }
  }

  return previous[right.length]
}

// One edit only. Fortnite versions its ids (Hera_V2_Terrain -> Hera_V3_Terrain), so even this
// flags real new content; the messages hedge accordingly.
const MIN_NEAR_MISS_LENGTH = 3

const findNearMiss = (value: string, candidates: string[], caseSensitive: boolean) => candidates.find(candidate => (
  candidate.length >= MIN_NEAR_MISS_LENGTH
  && (caseSensitive
    ? editDistance(value, candidate)
    : editDistance(value.toLowerCase(), candidate.toLowerCase())) === 1
))

// The client compares values case-insensitively, so a case-only difference is not a typo.
const findNearMissValue = (value: string, candidates: string[]) => (
  candidates.some(candidate => candidate.toLowerCase() === value.toLowerCase())
    ? undefined
    : findNearMiss(value, candidates, false)
)

// Payload keys are matched exactly, so queueID vs queueId is a real miss.
const findNearMissPath = (path: string, candidates: string[]) => findNearMiss(path, candidates, true)

const isNumericId = (value: string) => /^\d+$/.test(value)

// A container key one edit from "targeting" would otherwise import clean and serve everywhere.
export const looksLikeTargetingKey = (key: string) => (
  key !== "targeting" && editDistance(key.toLowerCase(), "targeting") <= 1
)

export const getGameLabel = (gameId?: NotificationsPerGame["gameId"]) => (
  gameNames[gameId as unknown as keyof typeof gameNames] || `game ${gameId ?? "?"}`
)

const getKnownPaths = (gameId?: NotificationsPerGame["gameId"]) => (
  gameId === undefined ? undefined : KNOWN_PATHS_BY_GAME[String(gameId)]
)

// An id we have not seen is usually just a mode nobody has recorded yet, so silence is the right
// answer. An id recorded under a *different* game on the same key is a real mistake: it says this
// campaign's game is wrong. This is what catches a TFT queue id on a League campaign.
// The caller has already returned for a value this game measures, so any game found here is another one.
const findGameClaimingValue = (path: string, value: string) => (
  Object.entries(KNOWN_PATHS_BY_GAME)
    .find(([, paths]) => paths.some(entry => entry.path === path
      && entry.values?.some(known => known.toLowerCase() === value.toLowerCase())))?.[0]
)

const getValueWarnings = (label: string, rule: TargetingRule, known: KnownPath): string[] => {
  const measured = known.values || []

  const warnings = rule.values.flatMap(value => {
    if (measured.some(candidate => candidate.toLowerCase() === value.toLowerCase())) return []

    const otherGame = findGameClaimingValue(rule.path, value)
    if (otherGame) {
      return [`"${value}" is a ${getGameLabel(otherGame)} value, so a match on it is reported under that game, `
        + "not this one."]
    }

    // Numbers are never near-misses of each other: on 3-4 digit ids every real one looks like a typo.
    if (isNumericId(value)) return []

    const nearMiss = findNearMissValue(value, measured)

    return nearMiss
      ? [`"${value}" is one character off the measured "${nearMiss}" — ignore this if it is new content.`]
      : []
  })

  return warnings.length ? [`${label}: ${warnings.join(" ")}`] : []
}

type WarningOptions = { includeCautions?: boolean }

const getRuleWarnings = (
  rule: TargetingRule,
  index: number,
  gameId?: NotificationsPerGame["gameId"],
  knownPaths?: KnownPath[],
  { includeCautions = true }: WarningOptions = {},
): string[] => {
  const label = `rules[${index}]`
  const segments = rule.path.split(".")
  const [category] = segments

  // The client keys facts as `category.key`, so anything else can never resolve, whatever the game.
  if (segments.length !== 2 || segments.some(segment => !segment.trim())) {
    return [`${label}: a path is always "<category>.<key>", e.g. "game_info.title_mnemonic". `
      + `"${rule.path}" cannot match anything.`]
  }
  if (UNBOUND_CATEGORIES.includes(category)) {
    return [`${label}: nothing is ever recorded under ${category}. `
      + "It is a feature name rather than a payload category, or no tracker binds it."]
  }

  // Checked before the per-game table so an unmeasured game still gets both source checks.
  if (rule.source === "launcher" && gameId !== undefined && !LAUNCHER_CAPABLE_GAMES.includes(String(gameId))) {
    return [
      `${label}: the client only reads a launcher for League of Legends, so a launcher rule on this game `
      + "can never match.",
    ]
  }
  if (LAUNCHER_ONLY_CATEGORIES.includes(category) && rule.source !== "launcher") {
    return [
      `${label}: ${category} is only ever read from the launcher stream, but this rule says `
      + `source "${rule.source}".`,
    ]
  }

  if (!knownPaths) return []

  const known = knownPaths.find(entry => entry.path === rule.path && entry.source === rule.source)

  if (!known) {
    const onOtherStream = knownPaths.find(entry => entry.path === rule.path)
    if (onOtherStream) {
      return [
        `${label}: ${rule.path} is read from the ${onOtherStream.source} stream, but this rule says `
        + `source "${rule.source}", so it can never match.`,
      ]
    }

    const nearMissPath = findNearMissPath(rule.path, knownPaths.map(entry => entry.path))
    const nearMissEntry = knownPaths.find(entry => entry.path === nearMissPath)

    if (!nearMissPath) {
      return [`${label}: this game is not known to report ${rule.path}. Double-check the key, or ignore this if it `
        + "is newly added."]
    }

    // Suggesting a path the table forbids would be worse than not suggesting one, so say both.
    const note = [nearMissEntry?.unsafe, nearMissEntry?.caution].filter(Boolean).join(" ")

    return [`${label}: this game is not known to report ${rule.path} — did you mean ${nearMissPath}?`
      + (note ? ` Note: ${note}` : "")]
  }

  const warnings: string[] = []

  if (known.unsafe) warnings.push(`${label}: ${known.unsafe}`)
  if (known.caution && includeCautions) warnings.push(`${label}: ${known.caution}`)

  if (rule.match === "contains" && rule.values.some(isNumericId)) {
    warnings.push(`${label}: a "contains" match on a numeric id also matches longer ids containing it. Use "equals".`)
  }

  // A partial value is the point of "contains", so only equality rules are value-checked.
  if (rule.match === "equals") warnings.push(...getValueWarnings(label, rule, known))

  return warnings
}

export const getTargetingWarnings = (
  value: unknown,
  gameId?: NotificationsPerGame["gameId"],
  options: WarningOptions = {},
): string[] => {
  let targeting: SpecialEventTargeting | undefined

  try {
    targeting = parseTargeting(value)
  } catch (error) {
    if (!(error instanceof TargetingError)) throw error

    return [`${error.message} This text will not be saved until it is fixed.`]
  }

  if (!targeting) return []

  const knownPaths = getKnownPaths(gameId)
  const ruleWarnings = targeting.rules
    .flatMap((rule, index) => getRuleWarnings(rule, index, gameId, knownPaths, options))

  return Array.from(new Set([...getUnsatisfiableRulePairs(targeting.rules), ...ruleWarnings]))
}

export const warnOnTargeting = async(value: unknown, gameId?: NotificationsPerGame["gameId"]) => {
  const warnings = getTargetingWarnings(value, gameId)

  if (warnings.length) throw new Error(warnings.join(" "))
}

// The client's verdict on a value already in config, which is a different question from whether the
// editor's text parses: it treats an empty rules array, a string and a non-object as *invalid* and
// stops the campaign serving on that game, where the editor treats empty text as simply cleared.
// Unknown rule keys are tolerated here exactly as the client tolerates them — reporting a campaign
// the client happily serves as "not serving" is worse than saying nothing.
export const classifyStoredTargeting = (value: unknown): "none" | "rules" | "invalid" => {
  if (value === undefined || value === null) return "none"
  if (!isRecord(value) || !Array.isArray(value.rules) || !value.rules.length) return "invalid"

  try {
    value.rules.forEach((rule, index) => parseRule(rule, index, { rejectUnknownKeys: false }))

    return "rules"
  } catch (error) {
    if (!(error instanceof TargetingError)) throw error

    return "invalid"
  }
}

export const isTargetingParseable = (value: unknown): boolean => {
  try {
    parseTargeting(value)

    return true
  } catch (error) {
    if (!(error instanceof TargetingError)) throw error

    return false
  }
}

// Load keeps whatever is stored, so a hand-edited value can still be opened and fixed.
export const normalizeTargeting = (value: unknown): TargetingFieldValue | undefined => {
  try {
    return parseTargeting(value)
  } catch (error) {
    if (!(error instanceof TargetingError)) throw error

    return stringifyTargeting(value)
  }
}

// The one change that silently widens delivery: a game that was restricted starts serving every
// mode. Returns the game ids that transition, so the caller can have it acknowledged.
export const listWidenedGameIds = (
  edited: { gameId?: unknown; targeting?: unknown }[] | undefined,
  stored: { gameId?: unknown; targeting?: unknown }[] | undefined,
): unknown[] => (edited || [])
  .filter(gameConfig => {
    const before = stored?.find(config => String(config?.gameId) === String(gameConfig?.gameId))?.targeting
    if (classifyStoredTargeting(before) === "none") return false

    // Only a deliberate clear widens; text that does not parse keeps the stored targeting in force.
    return isTargetingParseable(gameConfig?.targeting) && parseTargeting(gameConfig?.targeting) === undefined
  })
  .map(gameConfig => gameConfig?.gameId)

// The edit route has no key, so one Form instance is shared across campaigns: a draft may only be
// restored onto the campaign it was typed in. The uid this save wrote is authoritative — an imported
// campaign is seeded with the uid from its file, which is not the one save() mints for it.
export const isDraftFor = (savedUid: string | undefined, formUid: unknown, campaignUid: string) => (
  savedUid === campaignUid || formUid === campaignUid
)

// Re-seeding the form after a save rebuilds list items from the incoming element alone, so the text
// the admin is still fixing would be replaced — either by nothing (it was dropped) or, worse, by the
// stored value carried forward in its place, which they never typed. Unparseable text always wins
// over what the store returns; anything that parses was saved and the stored value is the truth.
export const restoreDroppedTargeting = (
  incoming: NotificationsPerGame[],
  current?: { gameId?: unknown; targeting?: unknown }[],
): NotificationsPerGame[] => incoming.map(gameConfig => {
  // Matched on gameId, not position: the game list can change between the save and the reload,
  // and a draft transplanted onto another game would be fixed in place and saved there.
  const draft = current?.find(config => String(config?.gameId) === String(gameConfig.gameId))?.targeting

  if (typeof draft !== "string" || isTargetingParseable(draft)) return gameConfig

  return { ...gameConfig, targeting: draft as unknown as NotificationsPerGame["targeting"] }
})

// Never throws, and never turns the editor's text into a stored string: what the admin types is
// either an object or nothing. A string already in config is a separate case, carried by the save
// path rather than produced here. The editor re-seeds dropped text so it can be fixed.
export const prepareTargetingForSave = (value: unknown): NotificationsPerGame["targeting"] => {
  try {
    return parseTargeting(value)
  } catch (error) {
    if (!(error instanceof TargetingError)) throw error

    return undefined
  }
}
